import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  activateKioskSession,
  clearKioskSession,
  consumeKioskExitSignal,
  getCurrentKioskStaffUserId,
  getKioskResumeUrl,
  getKioskReturnPath,
  isDedicatedKioskPath,
  isKioskAllowedPath,
  isKioskLauncherAuthorized,
  isKioskRoute,
  isKioskSessionActive,
  isStaffKioskEligible,
  KIOSK_DEFAULT_RETURN_PATH,
  KIOSK_EXIT_SIGNAL,
  subscribeKioskLockChanges,
} from "../../utils/patientFormKiosk";

/**
 * While kiosk lock is active (localStorage, all tabs), only allowed patient-form routes may load.
 */
export default function KioskSessionGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lockRevision, setLockRevision] = useState(0);

  const bumpLockRevision = useCallback(() => {
    setLockRevision((n) => n + 1);
  }, []);

  useEffect(() => {
    const onLockStorage = (event) => {
      if (event.key === KIOSK_EXIT_SIGNAL && event.newValue && isKioskRoute(location)) {
        const path = event.newValue || KIOSK_DEFAULT_RETURN_PATH;
        consumeKioskExitSignal();
        navigate(path, { replace: true });
      }
      bumpLockRevision();
    };
    return subscribeKioskLockChanges(onLockStorage);
  }, [bumpLockRevision, location.pathname, location.search, navigate]);

  /** If another tab exited kiosk, follow once lock is gone (covers missed storage events on focus). */
  useEffect(() => {
    if (!isDedicatedKioskPath(location.pathname)) return;
    if (isKioskSessionActive()) return;
    const path = consumeKioskExitSignal();
    if (path) {
      navigate(path, { replace: true });
    }
  }, [lockRevision, location.pathname, navigate]);

  useEffect(() => {
    const onFocus = () => bumpLockRevision();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [bumpLockRevision]);

  useEffect(() => {
    if (!isKioskRoute(location)) return;

    if (!isStaffKioskEligible()) {
      clearKioskSession();
      const returnPath = `${location.pathname}${location.search}`;
      try {
        localStorage.setItem("redirectTo", returnPath);
      } catch {
        /* ignore */
      }
      navigate("/login", { replace: true });
      return;
    }

    if (!isKioskLauncherAuthorized(location.search)) {
      clearKioskSession();
      if (isDedicatedKioskPath(location.pathname)) {
        navigate(KIOSK_DEFAULT_RETURN_PATH, {
          replace: true,
          state: { kioskAccessDenied: true },
        });
      }
      return;
    }

    const params = new URLSearchParams(location.search);
    const patientFormId = params.get("patient_form_id");
    if (!patientFormId) return;
    activateKioskSession({
      patientFormId,
      returnPath: getKioskReturnPath(),
      unifiedToken: params.get("unified_token"),
      ownerUserId: getCurrentKioskStaffUserId(),
    });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isKioskSessionActive() && !isStaffKioskEligible()) {
      clearKioskSession();
      bumpLockRevision();
    }
  }, [location.pathname, location.search, bumpLockRevision]);

  useEffect(() => {
    if (!isKioskSessionActive()) return;
    if (isKioskAllowedPath(location.pathname)) return;
    const resume = getKioskResumeUrl();
    if (resume) {
      navigate(resume, { replace: true });
    }
  }, [location.pathname, location.search, navigate, lockRevision]);

  return children;
}
